import { useState } from "react";
import { Pencil, Trash2, List, LayoutGrid, Eye } from "lucide-react";
import { SUBCATEGORIES, SPORTS } from "@/constants/categories";
import { resolveUrl } from "@/lib/api";

const Logo = ({ b, cls }) => (b.logo || b.photos?.[0]
  ? <img src={resolveUrl(b.logo || b.photos[0])} alt="" loading="lazy" data-testid="admin-listing-logo" className={`${cls} shrink-0 object-cover bg-muted ring-1 ring-border`} />
  : <span data-testid="admin-listing-logo" className={`${cls} shrink-0 bg-muted grid place-items-center text-xs font-bold text-muted-foreground`}>{b.name?.[0]}</span>);

const Status = ({ active }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>{active ? "Ενεργή" : "Ανενεργή"}</span>
);

const Actions = ({ b, onEdit, onDelete }) => (
  <div className="flex items-center gap-1">
    <button onClick={() => onEdit(b)} data-testid="admin-edit-listing-btn" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground hover:text-primary"><Pencil size={15} /></button>
    <button onClick={() => onDelete(b.id)} data-testid="admin-delete-listing-btn" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
  </div>
);

const groupBusinesses = (businesses) => {
  const order = Object.keys(SUBCATEGORIES);
  const groups = {};
  businesses.forEach((b) => {
    const key = SUBCATEGORIES[b.subcategory] ? b.subcategory : "_none";
    (groups[key] = groups[key] || []).push(b);
  });
  return [...order.filter((k) => groups[k]), ...(groups._none ? ["_none"] : [])]
    .map((k) => ({ key: k, title: k === "_none" ? "Χωρίς κατηγορία" : SUBCATEGORIES[k].title, items: groups[k] }));
};

const Row = ({ b, isAdmin, onEdit, onDelete }) => (
  <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30" data-testid="admin-listing-row">
    <Logo b={b} cls="h-10 w-10 rounded-lg" />
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm truncate">{b.name}</p>
      <p className="text-xs text-muted-foreground truncate">{[SPORTS[b.sport]?.title, b.city].filter(Boolean).join(" · ")}</p>
    </div>
    <span className="hidden md:flex items-center gap-1 text-xs text-muted-foreground w-16"><Eye size={13} /> {b.views || 0}</span>
    <Status active={b.active} />
    {isAdmin && <Actions b={b} onEdit={onEdit} onDelete={onDelete} />}
  </div>
);

const Tile = ({ b, isAdmin, onEdit, onDelete }) => (
  <div className="rounded-xl border border-border bg-white dark:bg-slate-800 overflow-hidden hover:shadow-md transition-shadow" data-testid="admin-listing-tile">
    <div className="relative h-24 bg-muted">
      {(b.cover_image || b.photos?.[0]) && <img src={resolveUrl(b.cover_image || b.photos[0])} alt="" loading="lazy" className="w-full h-full object-cover" />}
      <div className="absolute -bottom-5 left-3"><Logo b={b} cls="h-11 w-11 rounded-full ring-2 ring-white" /></div>
      <div className="absolute top-2 right-2"><Status active={b.active} /></div>
    </div>
    <div className="px-3 pt-6 pb-2">
      <p className="font-semibold text-sm line-clamp-1">{b.name}</p>
      <p className="text-xs text-muted-foreground line-clamp-1">{[SPORTS[b.sport]?.title, b.city].filter(Boolean).join(" · ") || "—"}</p>
      <div className="flex items-center justify-between mt-1.5">
        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Eye size={13} /> {b.views || 0}</span>
        {isAdmin && <Actions b={b} onEdit={onEdit} onDelete={onDelete} />}
      </div>
    </div>
  </div>
);

export const ListingsView = ({ businesses, isAdmin, onEdit, onDelete }) => {
  const [view, setView] = useState(() => localStorage.getItem("admin_listings_view") || "list");
  const pick = (v) => { setView(v); localStorage.setItem("admin_listings_view", v); };
  const groups = groupBusinesses(businesses);
  const btn = (v) => `h-9 w-9 grid place-items-center rounded-lg transition-colors ${view === v ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`;
  return (
    <div data-testid="admin-listings-table">
      <div className="flex justify-end gap-1 mb-3" data-testid="admin-view-toggle">
        <button onClick={() => pick("list")} className={btn("list")} data-testid="admin-view-list" aria-label="Λίστα"><List size={17} /></button>
        <button onClick={() => pick("grid")} className={btn("grid")} data-testid="admin-view-grid" aria-label="Πλέγμα"><LayoutGrid size={17} /></button>
      </div>
      <div className="space-y-6">
        {groups.map((g) => (
          <section key={g.key} data-testid={`admin-group-${g.key}`}>
            <h3 className="font-display font-bold text-base mb-2 flex items-center gap-2">{g.title}<span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">{g.items.length}</span></h3>
            {view === "list" ? (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-border divide-y divide-border overflow-hidden">
                {g.items.map((b) => <Row key={b.id} b={b} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                {g.items.map((b) => <Tile key={b.id} b={b} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} />)}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};
